import { useEffect, useMemo, useRef, useState } from "react";
import type {KeyboardEvent} from "react"
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

export type SurahLite = { number: number; name: string };

type Props = {
  surahs: SurahLite[];
  maxSuggestions?: number;
  hrefFor?: (n: number) => string;
};

export default function SurahSearch({ surahs, maxSuggestions = 7, hrefFor }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey as any);
    return () => window.removeEventListener("keydown", onKey as any);
  }, []);

  const normalized = (s: string) =>
    s
      .normalize("NFKD")
      .replace(/[\u064B-\u065F\u0610-\u061A\u06D6-\u06ED]/g, "")
      .replace(/\u0640/g, "")
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .trim();

  const isNumber = (v: string) => /^\d{1,3}$/.test(v.trim());

  const results = useMemo(() => {
    const needle = normalized(q);
    if (!needle) return [];
    if (isNumber(needle)) {
      const n = Number(needle);
      const s = surahs.find((x) => x.number === n);
      return s ? [s] : [];
    }
    return surahs.filter((s) => normalized(s.name).includes(needle)).slice(0, maxSuggestions);
  }, [q, surahs, maxSuggestions]);

  const go = (n: number) => {
    const href = hrefFor ? hrefFor(n) : `/surah/${n}`;
    navigate(href);
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open && results.length) setOpen(true);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results.length) go(results[activeIndex].number);
    } else if (e.key === "Escape") {
      setOpen(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  useEffect(() => setActiveIndex(0), [q]);

  return (
    <Wrap dir="rtl">
      <Box role="combobox" aria-expanded={open} aria-owns="surah-suggest-list">
        <IconLeft>
          <MagnifyingGlass size={18} />
        </IconLeft>

        <Input
          ref={inputRef}
          placeholder="ابحث باسم السورة أو رقمها…  /  للتركيز"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-controls="surah-suggest-list"
        />

        {q && (
          <ClearBtn
            aria-label="مسح"
            onClick={() => {
              setQ("");
              setOpen(false);
              inputRef.current?.focus();
            }}
          >
            <X size={16} />
          </ClearBtn>
        )}
      </Box>

      {open && results.length > 0 && (
        <SuggestList id="surah-suggest-list" ref={listRef} role="listbox">
          {results.map((s, i) => (
            <Item
              key={s.number}
              role="option"
              aria-selected={i === activeIndex}
              $active={i === activeIndex}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => go(s.number)}
            >
              <Badge>{s.number}</Badge>
              <span>{s.name}</span>
            </Item>
          ))}
        </SuggestList>
      )}

      {open && q && results.length === 0 && <Empty>لا توجد نتائج مطابقة</Empty>}
    </Wrap>
  );
}

/* ---- styles ---- */
const Wrap = styled.div`
  max-width: 720px;
  margin: 0 auto 16px;
  position: relative;
`;
const Box = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  border-radius: 12px;
  padding: 10px 40px;
  box-shadow: var(--shadow-xs, 0 1px 3px rgba(0, 0, 0, 0.06));
`;
const IconLeft = styled.div`
  position: absolute;
  inset-inline-start: 10px;
  opacity: 0.7;
  display: grid;
  place-items: center;
`;
const ClearBtn = styled.button`
  position: absolute;
  inset-inline-end: 10px;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  &:hover { color: var(--text); }
`;
const Input = styled.input`
  width: 100%;
  font-size: 0.98rem;
  background: transparent;
  color: var(--text);
  border: none;
  outline: none;
  &::placeholder { color: var(--muted); }
`;
const SuggestList = styled.ul`
  position: absolute;
  inset-inline: 0;
  top: calc(100% + 8px);
  background: var(--surface);
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  border-radius: 12px;
  box-shadow: var(--shadow, 0 10px 24px rgba(0, 0, 0, 0.12));
  padding: 6px;
  margin: 0;
  list-style: none;
  z-index: 10;
  max-height: 360px;
  overflow: auto;
`;
const Item = styled.li<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  background: ${(p) => (p.$active ? "var(--surface-2, rgba(0,0,0,0.05))" : "transparent")};
  color: var(--text);
  &:hover { background: var(--surface-2, rgba(0, 0, 0, 0.05)); }
`;
const Badge = styled.span`
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 2px 8px;
  border-radius: 12px;
`;
const Empty = styled.div`
  position: absolute;
  inset-inline: 0;
  top: calc(100% + 8px);
  background: var(--surface);
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  color: var(--muted);
  z-index: 20;
  box-shadow: var(--shadow, 0 10px 24px rgba(0, 0, 0, 0.12));
`;
